import { useQuery } from '@tanstack/react-query';
import { physioStatusApi } from '../services/api';

function formatLastUpdated(dateStr: string | null): string {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString();
}

interface TeamPhysio {
  id: number;
  name: string;
  status: 'active' | 'sick' | 'unavailable';
  updated_at: string | null;
}

export function PublicTeamStatus() {
  const { data: physios, isLoading, refetch } = useQuery({
    queryKey: ['public-team-status'],
    queryFn: async () => {
      const response = await physioStatusApi.getTeamStatus();
      return response.data as TeamPhysio[];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading team status...</p>
        </div>
      </div>
    );
  }

  // Group physios by status
  const active = physios?.filter(p => p.status === 'active') || [];
  const sick = physios?.filter(p => p.status === 'sick') || [];
  const unavailable = physios?.filter(p => p.status === 'unavailable') || [];

  const totalCount = physios?.length || 0;
  const activeCount = active.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-700 to-teal-600 shadow-lg">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Foundations Physio</h1>
                <p className="text-teal-100 text-sm">Team Status</p>
              </div>
            </div>
            <button
              onClick={() => refetch()}
              className="p-2 text-white hover:bg-teal-800 rounded-lg transition-colors"
              title="Refresh"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Summary */}
        <div className="text-center">
          <p className="text-gray-600">
            <span className="text-2xl font-bold text-teal-700">{activeCount}</span>
            <span className="text-gray-500"> of </span>
            <span className="text-2xl font-bold text-gray-700">{totalCount}</span>
            <span className="text-gray-500"> physios active today</span>
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl shadow-sm p-4 text-center border-2 border-green-100">
            <div className="text-3xl font-bold text-green-600">{active.length}</div>
            <div className="text-sm text-gray-500">Active</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 text-center border-2 border-red-100">
            <div className="text-3xl font-bold text-red-600">{sick.length}</div>
            <div className="text-sm text-gray-500">Sick</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 text-center border-2 border-yellow-100">
            <div className="text-3xl font-bold text-yellow-600">{unavailable.length}</div>
            <div className="text-sm text-gray-500">Unavailable</div>
          </div>
        </div>

        {/* Active Physios */}
        {active.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-green-50 border-b border-green-100">
              <h2 className="font-semibold text-green-800">Active ({active.length})</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {active.map(physio => (
                <div key={physio.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">✅</span>
                    <span className="font-medium text-gray-900">{physio.name}</span>
                  </div>
                  <span className="text-xs text-gray-400">{formatLastUpdated(physio.updated_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sick Physios */}
        {sick.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-red-50 border-b border-red-100">
              <h2 className="font-semibold text-red-800">Sick ({sick.length})</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {sick.map(physio => (
                <div key={physio.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🤒</span>
                    <span className="font-medium text-gray-900">{physio.name}</span>
                  </div>
                  <span className="text-xs text-gray-400">{formatLastUpdated(physio.updated_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Unavailable Physios */}
        {unavailable.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-100">
              <h2 className="font-semibold text-yellow-800">Unavailable ({unavailable.length})</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {unavailable.map(physio => (
                <div key={physio.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">⛔</span>
                    <span className="font-medium text-gray-900">{physio.name}</span>
                  </div>
                  <span className="text-xs text-gray-400">{formatLastUpdated(physio.updated_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {totalCount === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
            No team members found.
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-gray-400 pt-4">
          <p>Auto-refreshes every 30 seconds</p>
          <p className="mt-1">
            <a href="/status" className="text-teal-600 hover:underline">Update your status</a>
          </p>
        </div>
      </div>
    </div>
  );
}
